import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { QRCodeCanvas } from 'qrcode.react';
import html2canvas from 'html2canvas';
import FaqAccordion from '../components/FaqAccordion';
import {
  Link2, MessageCircle, Instagram, Youtube, FileText, Phone, Mail, MessageSquare,
  Wifi, User, IndianRupee, MapPin, Twitter, Linkedin, Facebook, Music, Send, Ghost,
  Sparkles, Leaf, Waves, Sunset, Flame, Heart, Flower2, Palette, Image, Tag,
  CheckCircle2, AlertTriangle, XCircle, Upload, X, Download
} from 'lucide-react';

const QR_TYPES = [
  { id: 'url',       label: 'URL',       icon: Link2 },
  { id: 'whatsapp',  label: 'WhatsApp',  icon: MessageCircle },
  { id: 'instagram', label: 'Instagram', icon: Instagram },
  { id: 'youtube',   label: 'YouTube',   icon: Youtube },
  { id: 'pdf',       label: 'PDF',       icon: FileText },
  { id: 'phone',     label: 'Phone',     icon: Phone },
  { id: 'email',     label: 'Email',     icon: Mail },
  { id: 'sms',       label: 'SMS',       icon: MessageSquare },
  { id: 'wifi',      label: 'WiFi',      icon: Wifi },
  { id: 'vcard',     label: 'vCard',     icon: User },
  { id: 'upi',       label: 'UPI Pay',   icon: IndianRupee },
  { id: 'location',  label: 'Location',  icon: MapPin },
  { id: 'twitter',   label: 'Twitter/X', icon: Twitter },
  { id: 'linkedin',  label: 'LinkedIn',  icon: Linkedin },
];

const BG_TEMPLATES = [
  { id: 'instagram', color: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', icon: Instagram },
  { id: 'facebook',  color: '#1877f2', icon: Facebook },
  { id: 'tiktok',    color: '#010101', icon: Music },
  { id: 'linkedin',  color: '#0077b5', icon: Linkedin },
  { id: 'whatsapp',  color: '#25d366', icon: MessageCircle },
  { id: 'twitter',   color: '#000000', icon: Twitter },
  { id: 'telegram',  color: '#26a5e4', icon: Send },
  { id: 'snapchat',  color: '#fffc00', icon: Ghost },
  { id: 'neon',      color: 'linear-gradient(135deg,#0f0c29,#302b63,#24243e)', icon: Sparkles },
  { id: 'forest',    color: 'linear-gradient(135deg,#134e5e,#71b280)', icon: Leaf },
  { id: 'ocean',     color: 'linear-gradient(135deg,#006994,#0099cc)', icon: Waves },
  { id: 'sunset',    color: 'linear-gradient(135deg,#f7971e,#ffd200)', icon: Sunset },
  { id: 'fire',      color: 'linear-gradient(135deg,#f12711,#f5af19)', icon: Flame },
  { id: 'purple',    color: 'linear-gradient(135deg,#a855f7,#6366f1)', icon: Heart },
  { id: 'rose',      color: 'linear-gradient(135deg,#f43f5e,#ec4899)', icon: Flower2 },
  { id: 'gold',      color: 'linear-gradient(135deg,#f6d365,#fda085)', icon: Sparkles },
];

const QR_STYLE_PRESETS = [
  { id: 'classic', label: 'Classic', fg: '#000000', bg: '#ffffff' },
  { id: 'dark',    label: 'Dark',    fg: '#ffffff', bg: '#111827' },
  { id: 'brand',   label: 'Brand',   fg: '#ff2d4b', bg: '#ffffff' },
  { id: 'ocean',   label: 'Ocean',   fg: '#0077b5', bg: '#e8f4fd' },
  { id: 'forest',  label: 'Forest',  fg: '#16a34a', bg: '#f0fdf4' },
  { id: 'purple',  label: 'Purple',  fg: '#7c3aed', bg: '#f5f3ff' },
  { id: 'gold',    label: 'Gold',    fg: '#92400e', bg: '#fffbeb' },
  { id: 'navy',    label: 'Navy',    fg: '#1e3a5f', bg: '#f0f4ff' },
];

// Real error-correction damage-tolerance budgets, per the QR code spec —
// these are the actual percentages of the code each level can lose/have
// obscured and still decode correctly (not made-up numbers).
const ERROR_BUDGET = { L: 7, M: 15, Q: 25, H: 30 };

// Standard WCAG relative-luminance / contrast-ratio formulas — the same
// math used for accessibility contrast checking, repurposed here because
// it's a genuinely well-established way to measure how distinguishable
// two colors are, which is exactly what a QR scanner also depends on.
function relativeLuminance(hex) {
  const rgb = hex.replace('#', '').match(/.{1,2}/g).map(h => {
    const c = parseInt(h, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
}
function contrastRatio(hexA, hexB) {
  const lA = relativeLuminance(hexA), lB = relativeLuminance(hexB);
  const lighter = Math.max(lA, lB), darker = Math.min(lA, lB);
  return (lighter + 0.05) / (darker + 0.05);
}

// A genuine multi-factor scannability estimate — not just "which error
// level did you pick." Combines the real error-correction budget, actual
// fg/bg contrast, and (when a logo is present) how much of the code area
// the fixed-size logo actually covers versus what that budget can absorb.
function estimateScannability({ fgColor, bgColor, errorLevel, hasLogo, qrPixelSize }) {
  const errorBudget = ERROR_BUDGET[errorLevel] || 7;
  let score = (errorBudget / 30) * 100;

  const contrast = contrastRatio(fgColor, bgColor);
  if (contrast < 2) score *= 0.25;
  else if (contrast < 4.5) score *= 0.6;
  else if (contrast < 7) score *= 0.85;

  if (hasLogo) {
    // The logo is rendered at a fixed 42x42px regardless of overall QR
    // size, so its share of the code's area shrinks as the QR gets bigger
    // — a real, physical effect, not an approximation for its own sake.
    const logoAreaPct = (42 * 42) / (qrPixelSize * qrPixelSize) * 100;
    if (logoAreaPct > errorBudget) {
      score -= Math.min(40, (logoAreaPct - errorBudget) * 2);
    }
  }

  return Math.max(5, Math.min(100, Math.round(score)));
}

function buildQRData(type, inputs) {
  const v = k => inputs[k] || '';
  switch (type) {
    case 'url': return v('url') || 'https://iloveconvert.in';
    case 'whatsapp': return `https://wa.me/${v('phone').replace(/[^0-9]/g,'')}?text=${encodeURIComponent(v('message'))}`;
    case 'instagram': return `https://instagram.com/${v('username')}`;
    case 'youtube': return v('url') || 'https://youtube.com';
    case 'pdf': return v('url');
    case 'phone': return `tel:${v('phone')}`;
    case 'email': return `mailto:${v('email')}?subject=${encodeURIComponent(v('subject'))}&body=${encodeURIComponent(v('body'))}`;
    case 'sms': return `smsto:${v('phone')}:${v('message')}`;
    case 'wifi': return `WIFI:S:${v('ssid')};T:${v('encryption')||'WPA'};P:${v('password')};;`;
    case 'vcard': return `BEGIN:VCARD\nVERSION:3.0\nFN:${v('name')}\nTEL:${v('phone')}\nEMAIL:${v('email')}\nORG:${v('company')}\nURL:${v('url')}\nEND:VCARD`;
    case 'upi': return `upi://pay?pa=${v('upiId')}&pn=${v('name')}&am=${v('amount')}&cu=INR`;
    case 'location': return `geo:${v('lat')},${v('lng')}`;
    case 'twitter': return `https://twitter.com/${v('username')}`;
    case 'linkedin': return `https://linkedin.com/in/${v('username')}`;
    default: return v('url') || 'https://iloveconvert.in';
  }
}

function QRForm({ type, inputs, setInputs }) {
  const set = k => e => setInputs(p => ({ ...p, [k]: e.target.value }));
  const inp = (label, key, placeholder, t='text') => (
    <div key={key} style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--t-secondary)', marginBottom: 4 }}>{label}</label>
      <input type={t} placeholder={placeholder} value={inputs[key]||''} onChange={set(key)}
        style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.88rem', background: 'var(--glass-2)', color: 'var(--t-primary)', boxSizing: 'border-box' }} />
    </div>
  );
  switch (type) {
    case 'url': return inp('Enter URL / Link', 'url', 'https://iloveconvert.in', 'url');
    case 'whatsapp': return <>{inp('WhatsApp Number (with country code)','phone','+91 98765 43210','tel')}{inp('Pre-filled Message','message','Hello!')}</>;
    case 'instagram': return inp('Instagram Username','username','yourusername');
    case 'youtube': return inp('YouTube URL','url','https://youtube.com/@channel','url');
    case 'pdf': return inp('PDF / Document URL','url','https://yoursite.com/file.pdf','url');
    case 'phone': return inp('Phone Number','phone','+91 98765 43210','tel');
    case 'email': return <>{inp('Email Address','email','email@example.com','email')}{inp('Subject (optional)','subject','Hello')}</>;
    case 'sms': return <>{inp('Phone Number','phone','+91 98765 43210','tel')}{inp('Message','message','Your message here')}</>;
    case 'wifi': return <>{inp('Network Name (SSID)','ssid','MyHomeWiFi')}{inp('WiFi Password','password','WiFiPass123')}</>;
    case 'vcard': return <>{inp('Full Name','name','John Doe')}{inp('Phone','phone','+91 98765 43210','tel')}{inp('Email','email','email@example.com','email')}{inp('Company','company','Techb Software Solutions Pvt Ltd')}{inp('Website','url','https://iloveconvert.in')}</>;
    case 'upi': return <>{inp('UPI ID','upiId','yourname@upi')}{inp('Your Name','name','John Doe')}{inp('Amount (optional)','amount','100','number')}</>;
    case 'location': return <>{inp('Latitude','lat','12.9716')}{inp('Longitude','lng','77.5946')}</>;
    case 'twitter': return inp('Twitter / X Username','username','yourusername');
    case 'linkedin': return inp('LinkedIn Username','username','yourname');
    default: return inp('URL','url','https://iloveconvert.in','url');
  }
}

export default function QRCodeGenerator() {
  const [qrType,     setQrType]     = useState('url');
  const [inputs,     setInputs]     = useState({ url: 'https://iloveconvert.in' });
  const [qrStyle,    setQrStyle]    = useState('custom');
  const [selectedBg, setSelectedBg] = useState(null);
  const [fgColor,    setFgColor]    = useState('#000000');
  const [bgColor,    setBgColor]    = useState('#ffffff');
  const [preset,     setPreset]     = useState('classic');
  const [size,       setSize]       = useState(260);
  const [errorLevel, setErrorLevel] = useState('H');
  const [logoUrl,    setLogoUrl]    = useState('');
  const [userBgUrl,  setUserBgUrl]  = useState('');
  const logoRef = useRef();
  const bgRef   = useRef();

  const qrData = buildQRData(qrType, inputs);
  const previewQrSize = Math.min(size, 220);
  const score = estimateScannability({
    fgColor, bgColor, errorLevel,
    hasLogo: qrStyle === 'logo' && !!logoUrl,
    qrPixelSize: previewQrSize,
  });
  const scoreColor = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';

  const handlePreset = p => {
    setPreset(p);
    const s = QR_STYLE_PRESETS.find(x => x.id === p);
    if (s) { setFgColor(s.fg); setBgColor(s.bg); }
  };

  const getBgStyle = () => {
    if (userBgUrl) return { backgroundImage: `url(${userBgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    if (selectedBg) {
      const t = BG_TEMPLATES.find(x => x.id === selectedBg);
      if (t) return t.color.startsWith('linear') ? { backgroundImage: t.color } : { background: t.color };
    }
    return { background: '#f9fafb' };
  };

const handleDownload = async () => {
  const previewEl = document.getElementById('qr-preview');
  if (!previewEl) return;

  try {
    const canvas = await html2canvas(previewEl, {
      scale: 2,           // 2x resolution for crisp output
      useCORS: true,      // allow cross-origin images
      backgroundColor: null,
      logging: false,
    });

    const a = document.createElement('a');
    a.download = `iloveconvert-qr-${qrType}.png`;
    a.href = canvas.toDataURL('image/png');
    a.click();
  } catch (err) {
    // Fallback to plain QR canvas
    const qrCanvas = document.querySelector('#qr-preview canvas');
    if (!qrCanvas) return;
    const a = document.createElement('a');
    a.download = `iloveconvert-qr-${qrType}.png`;
    a.href = qrCanvas.toDataURL('image/png');
    a.click();
  }
};

  return (
    <div>
      <Helmet>
        <title>Free QR Code Generator — Custom Design, Logo, Image QR | ILoveConvert</title>
        <meta name="description" content="Generate beautiful QR codes free — custom colors, image backgrounds, logo support, UPI payment, WiFi, WhatsApp, vCard. No signup. Instant PNG download." />
        <meta name="keywords" content="qr code generator, free qr code, custom qr code, qr code with logo, upi qr code, wifi qr code, whatsapp qr code" />
        <link rel="canonical" href="https://iloveconvert.in/qr-code-generator" />
        <meta property="og:title" content="Free QR Code Generator — Custom Design, Logo, Image QR | ILoveConvert" />
        <meta property="og:description" content="Generate beautiful QR codes free — custom colors, logo support, UPI, WiFi, WhatsApp, vCard. No signup." />
        <meta property="og:url" content="https://iloveconvert.in/qr-code-generator" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free QR Code Generator — Custom Design, Logo, Image QR" />
        <meta name="twitter:description" content="Generate beautiful QR codes free — custom colors, logo support, UPI, WiFi, WhatsApp, vCard." />
      </Helmet>

      <section className="hero" style={{ paddingBottom: 28 }}>
        <h1>Make Your QR Code <span>Now</span></h1>
        <p>Choose from 14+ QR code types, including links, WhatsApp, UPI, Wi-Fi, contacts, and more. Add your own colours, logo, or background to give it a personal touch.</p>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container" style={{ maxWidth: 1080 }}>
          <div className="qr-generator-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 24, alignItems: 'start' }}>

            {/* LEFT */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Step 1 */}
              <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--primary)', color: 'white', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem', flexShrink: 0 }}>1</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>Make QR code from</h3>
                </div>
                <div className="qr-type-scroll" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                  {QR_TYPES.map(t => (
                    <button key={t.id} onClick={() => { setQrType(t.id); setInputs({}); }}
                      style={{ flexShrink: 0, padding: '6px 12px', borderRadius: 18, border: qrType === t.id ? '2px solid var(--primary)' : '1px solid var(--border)', background: qrType === t.id ? 'rgba(255,45,75,0.1)' : 'var(--glass-2)', color: qrType === t.id ? 'var(--primary)' : 'var(--t-secondary)', fontWeight: qrType === t.id ? 700 : 400, cursor: 'pointer', fontSize: '0.8rem' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}><t.icon size={13} /> {t.label}</span>
                    </button>
                  ))}
                </div>
                <QRForm type={qrType} inputs={inputs} setInputs={setInputs} />
              </div>

              {/* Step 2 */}
              <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--primary)', color: 'white', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem', flexShrink: 0 }}>2</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>Choose Your QR Code Style</h3>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10, marginBottom: 18 }}>
                  {[
                    { id: 'custom', label: 'Custom QR',  sub: 'Design your own',       icon: Palette },
                    { id: 'image',  label: 'Image QR',   sub: 'With background image',  icon: Image },
                    { id: 'logo',   label: 'Logo QR',    sub: 'Add your logo',          icon: Tag },
                  ].map(s => (
                    <button key={s.id} onClick={() => setQrStyle(s.id)}
                      style={{ padding: '14px 10px', borderRadius: 12, border: qrStyle === s.id ? '2px solid var(--primary)' : '1px solid var(--border)', background: qrStyle === s.id ? 'rgba(255,45,75,0.08)' : 'var(--glass-2)', cursor: 'pointer', textAlign: 'center' }}>
                      <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center' }}><s.icon size={24} strokeWidth={1.75} /></div>
                      <div style={{ fontWeight: 700, fontSize: '0.82rem', color: qrStyle === s.id ? 'var(--primary)' : 'var(--t-primary)' }}>{s.label}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--t-secondary)', marginTop: 2 }}>{s.sub}</div>
                    </button>
                  ))}
                </div>

                <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--t-secondary)', marginBottom: 8 }}>Color Preset</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                  {QR_STYLE_PRESETS.map(p => (
                    <button key={p.id} onClick={() => handlePreset(p.id)} title={p.label}
                      style={{ width: 34, height: 34, borderRadius: 8, border: preset === p.id ? '3px solid var(--primary)' : '1px solid var(--border)', background: p.fg, cursor: 'pointer', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '40%', background: p.bg }} />
                    </button>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--t-secondary)', display: 'block', marginBottom: 5 }}>QR Color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', borderRadius: 8, padding: '5px 8px', background: 'var(--glass-2)' }}>
                      <input type="color" value={fgColor} onChange={e => { setFgColor(e.target.value); setPreset(''); }} style={{ width: 26, height: 26, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
                      <span style={{ fontSize: '0.78rem', color: 'var(--t-secondary)' }}>{fgColor.toUpperCase()}</span>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--t-secondary)', display: 'block', marginBottom: 5 }}>Background</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--border)', borderRadius: 8, padding: '5px 8px', background: 'var(--glass-2)' }}>
                      <input type="color" value={bgColor} onChange={e => { setBgColor(e.target.value); setPreset(''); }} style={{ width: 26, height: 26, border: 'none', cursor: 'pointer', borderRadius: 4 }} />
                      <span style={{ fontSize: '0.78rem', color: 'var(--t-secondary)' }}>{bgColor.toUpperCase()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--primary)', color: 'white', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.82rem', flexShrink: 0 }}>3</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>Design Your QR Code</h3>
                </div>

                {qrStyle === 'image' && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--t-secondary)', marginBottom: 8 }}>Background Image</p>
                    <div onClick={() => bgRef.current.click()}
                      style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: 14, textAlign: 'center', cursor: 'pointer', marginBottom: 10, minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', ...( userBgUrl ? { backgroundImage: `url(${userBgUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: 'var(--glass-2)' }) }}>
                      {userBgUrl
                        ? <span style={{ background: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}><CheckCircle2 size={14} /> Custom image loaded</span>
                        : <span style={{ fontSize: '0.82rem', color: 'var(--t-secondary)' }}><Upload size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Drop a file or <span style={{ color: 'var(--primary)' }}>select an image</span><br/><span style={{ fontSize: '0.72rem' }}>JPEG, JPG, PNG, SVG</span></span>
                      }
                    </div>
                    <input ref={bgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if(f) { setUserBgUrl(URL.createObjectURL(f)); setSelectedBg('custom'); }}} />
                    <p style={{ fontSize: '0.75rem', color: 'var(--t-secondary)', marginBottom: 8 }}>Or choose a template:</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(0,1fr))', gap: 5 }}>
                      {BG_TEMPLATES.map(t => (
                        <button key={t.id} onClick={() => { setSelectedBg(t.id); setUserBgUrl(''); }} title={t.id}
                          style={{ aspectRatio: '1', borderRadius: 7, border: selectedBg === t.id ? '2px solid var(--primary)' : '1px solid transparent', background: t.color.startsWith('linear') ? t.color : t.color, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <t.icon size={13} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {qrStyle === 'logo' && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--t-secondary)', marginBottom: 8 }}>Upload Your Logo</p>
                    <div onClick={() => logoRef.current.click()}
                      style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: 20, textAlign: 'center', cursor: 'pointer', background: 'var(--glass-2)' }}>
                      {logoUrl
                        ? <img src={logoUrl} alt="logo" style={{ maxHeight: 56, maxWidth: 110, objectFit: 'contain' }} />
                        : <><Tag size={28} style={{ marginBottom: 4 }} /><br/><span style={{ fontSize: '0.8rem', color: 'var(--t-secondary)' }}>Click to upload logo<br/><span style={{ fontSize: '0.72rem' }}>PNG with transparent background recommended</span></span></>
                      }
                    </div>
                    <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; if(f) setLogoUrl(URL.createObjectURL(f)); }} />
                    {logoUrl && <button onClick={() => setLogoUrl('')} style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}><X size={12} /> Remove logo</button>}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 }}>
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--t-secondary)', display: 'block', marginBottom: 5 }}>Size: {size}px</label>
                    <input type="range" min={128} max={512} step={32} value={size} onChange={e => setSize(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--primary)' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--t-secondary)', display: 'block', marginBottom: 5 }}>Error Correction</label>
                    <select value={errorLevel} onChange={e => setErrorLevel(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: '0.82rem', background: 'var(--glass-2)', color: 'var(--t-primary)' }}>
                      <option value="L">L — Low</option>
                      <option value="M">M — Medium</option>
                      <option value="Q">Q — Quartile</option>
                      <option value="H">H — High (Best)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT — PREVIEW */}
            <div className="qr-generator-preview" style={{ position: 'sticky', top: 20 }}>
              <div style={{ background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: 22 }}>

                {/* QR Preview Box */}
                <div id="qr-preview"
                  style={{ borderRadius: 16, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 290, padding: 16, position: 'relative', overflow: 'hidden', ...getBgStyle() }}>
                 <div style={{ borderRadius: 10, overflow: 'hidden', display: 'inline-block', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
  <QRCodeCanvas
    value={qrData}
    size={previewQrSize}
    fgColor={fgColor}
    bgColor={bgColor}
    level={errorLevel}
    includeMargin={true}
    imageSettings={logoUrl && qrStyle === 'logo' ? { src: logoUrl, height: 42, width: 42, excavate: true } : undefined}
  />
</div>
                </div>

                {/* Scannability */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--t-secondary)' }}>Scannability</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: scoreColor, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {score >= 80 ? <><CheckCircle2 size={13} /> High Scannability</> : score >= 60 ? <><AlertTriangle size={13} /> Medium</> : <><XCircle size={13} /> Low</>}
                    </span>
                  </div>
                  <div style={{ height: 5, background: 'var(--glass-2)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${score}%`, background: 'linear-gradient(to right,#ef4444 0%,#f59e0b 50%,#22c55e 100%)', borderRadius: 3, transition: 'width 0.4s' }} />
                  </div>
                </div>

                {/* Download */}
                <button onClick={handleDownload}
                  style={{ width: '100%', padding: '12px 0', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                  ⬇ Generate & Download QR Code
                </button>

                <p style={{ fontSize: '0.73rem', color: 'var(--t-secondary)', textAlign: 'center', marginTop: 6 }}>
                  Free · No watermark · No signup required
                </p>

                <div style={{ marginTop: 12, background: 'var(--glass-2)', borderRadius: 8, padding: '8px 10px' }}>
                  <p style={{ fontSize: '0.7rem', color: 'var(--t-secondary)', margin: 0, wordBreak: 'break-all' }}>
                    <strong>QR Data:</strong> {qrData.length > 55 ? qrData.substring(0, 55) + '...' : qrData}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div style={{ marginTop: 52 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', textAlign: 'center', marginBottom: 8 }}>Create Your QR Code in 3 Easy Steps</h2>
            <p style={{ textAlign: 'center', color: 'var(--t-secondary)', marginBottom: 28, fontSize: '0.92rem' }}>Add your details, make it look the way you want, and download it.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
              {[
                { n:1, icon: Link2, title:'Add Your Details', desc:'Enter a URL, phone number, Wi-Fi details, UPI ID, contact information, or anything else you want to share.' },
                { n:2, icon: Palette, title:'Make Your Own QR', desc:'Choose your colours and style, add a background, or upload your logo.' },
                { n:3, icon: Download, title:'Download & Share', desc:'Download your QR code as a PNG and use it on business cards, posters, packaging, and more.' },
              ].map(s => (
                <div key={s.n} style={{ textAlign: 'center', background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', padding: '22px 16px' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--primary)', color: 'white', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontSize: '1rem' }}>{s.n}</div>
                  <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center', color: 'var(--primary)' }}><s.icon size={28} strokeWidth={1.75} /></div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '0.93rem', fontWeight: 700, marginBottom: 6 }}>{s.title}</h3>
                  <p style={{ color: 'var(--t-secondary)', fontSize: '0.84rem', lineHeight: 1.6 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div style={{ marginTop: 52 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', textAlign: 'center', marginBottom: 20 }}>Frequently Asked Questions</h2>
            <FaqAccordion faqs={[
              { q:'Is it free to make a QR code?', a:'Yes. You can create and download QR codes for free. No account is needed.' },
              { q:'Will my QR code stop working?', a:'No. Your QR code doesn’t have an expiry date. It will keep working as long as the link or information in it is still available.' },
              { q:'Can I add my logo on it?', a:'Yes. Add your logo and give your QR code a look that matches your brand.' },
              { q:'Can I add a background image?', a:'Yes. You can use common image formats like JPG and PNG as your background.' },
              { q:'Which error correction should I use?', a:'If you’re not sure, Medium is a good choice for most QR codes. If you add a logo, a higher level can make scanning more reliable.' },
              { q:'Can I use it for my business?', a:'Of course. Use your QR codes on menus, posters, business cards, packaging, websites, and anywhere else you want to share information.' },
            ]} />
          </div>

          {/* Learn more: QR type guides */}
          <div style={{ marginTop: 52 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', textAlign: 'center', marginBottom: 8 }}>Learn About QR Code Types</h2>
            <p style={{ textAlign: 'center', color: 'var(--t-secondary)', marginBottom: 24, fontSize: '0.9rem' }}>Not sure which QR code to use? Check these simple guides to find the right one.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
              {[
                { href: '/articles/types-of-qr-codes-complete-guide', title: 'All QR Code Types', desc: 'See all 14 types and when to use them.' },
                { href: '/articles/wifi-qr-code-generator-guide', title: 'Wi-Fi QR Codes', desc: 'Let people join your Wi-Fi without typing the password.' },
                { href: '/articles/upi-qr-code-generator-guide', title: 'UPI QR Codes', desc: 'Make payments easier with a quick scan.' },
                { href: '/articles/vcard-qr-code-generator-guide', title: 'vCard QR Codes', desc: 'Share your contact details like a digital business card.' },
                { href: '/articles/whatsapp-qr-code-generator-guide', title: 'WhatsApp QR Codes', desc: 'Let people start a chat with you in one scan.' },
                { href: '/articles/location-qr-code-generator-guide', title: 'Location QR Codes', desc: 'Share a location or map pin with a quick scan.' },
              ].map(g => (
                <Link key={g.href} to={g.href} style={{ display: 'block', background: 'var(--glass-1)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: '16px 18px', textDecoration: 'none' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.9rem', color: 'var(--t-primary)', marginBottom: 4 }}>{g.title}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--t-secondary)', lineHeight: 1.5 }}>{g.desc}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}